import type { IntlShape } from "react-intl";
import type { RefinementCtx } from "zod";
import { z } from "zod";

import { alphabetize } from "@citrine/utils/sorting";

import type { FormattedUnitsLookup } from "../units";
import { isOfType } from "../utils";

export const DESCRIPTOR_TYPES = [
  "Categorical",
  "Formulation",
  "Inorganic",
  "Integer",
  "Organic",
  "Real",
] as const;

export type DescriptorType = (typeof DESCRIPTOR_TYPES)[number];

export interface IBaseDescriptor {
  descriptor_key: string;
  type: DescriptorType;
}

export interface ICategoricalDescriptor extends IBaseDescriptor {
  type: "Categorical";
  descriptor_values: string[];
}

export interface IOrganicChemicalFormulaDescriptor extends IBaseDescriptor {
  type: "Organic";
}

const getSuperRefineBounds = (intl: Pick<IntlShape, "formatMessage">) =>
  function superRefineBounds<
    Value extends { lower_bound: number; upper_bound: number },
  >(val: Value, ctx: RefinementCtx<Value>): void {
    if (val.lower_bound > val.upper_bound) {
      ctx.issues.push({
        code: "custom",
        input: val.lower_bound,
        message: intl.formatMessage(
          { id: "must be less than or equal to {ceiling}" },
          { ceiling: val.upper_bound },
        ),
        path: ["lower_bound"],
      });
      ctx.issues.push({
        code: "custom",
        input: val.upper_bound,
        message: intl.formatMessage(
          { id: "must be greater than or equal to {floor}" },
          { floor: val.lower_bound },
        ),
        path: ["upper_bound"],
      });
    }
  };

export const getIntegerDescriptorSchema = (
  intl: Pick<IntlShape, "formatMessage">,
) =>
  z
    .object({
      type: z.literal("Integer"),
      descriptor_key: z.string(),
      lower_bound: z.int(),
      upper_bound: z.int(),
    })
    .superRefine(getSuperRefineBounds(intl));
export type IIntegerDescriptor = z.output<
  ReturnType<typeof getIntegerDescriptorSchema>
>;

export const getRealDescriptorSchema = (
  intl: Pick<IntlShape, "formatMessage">,
) =>
  z
    .object({
      type: z.literal("Real"),
      descriptor_key: z.string(),
      lower_bound: z.number(),
      upper_bound: z.number(),
      units: z.string(),
    })
    .superRefine(getSuperRefineBounds(intl));
export type IRealDescriptor = z.output<
  ReturnType<typeof getRealDescriptorSchema>
>;

export interface IInorganicChemicalFormulaDescriptor extends IBaseDescriptor {
  type: "Inorganic";
}

export interface IFormulationDescriptor extends IBaseDescriptor {
  type: "Formulation";
}

export type IDescriptor =
  | ICategoricalDescriptor
  | IIntegerDescriptor
  | IOrganicChemicalFormulaDescriptor
  | IRealDescriptor
  | IInorganicChemicalFormulaDescriptor
  | IFormulationDescriptor;

export type INumericDescriptor = IIntegerDescriptor | IRealDescriptor;

export type IValueDescriptor = ICategoricalDescriptor | INumericDescriptor;

export const isRealDescriptor = isOfType<IRealDescriptor, IDescriptor>("Real");
export const isIntegerDescriptor = isOfType<IIntegerDescriptor, IDescriptor>(
  "Integer",
);
export const isCategoricalDescriptor = isOfType<
  ICategoricalDescriptor,
  IDescriptor
>("Categorical");
export const isOrganicChemicalFormulaDescriptor = isOfType<
  IOrganicChemicalFormulaDescriptor,
  IDescriptor
>("Organic");
export const isInorganicChemicalFormulaDescriptor = isOfType<
  IInorganicChemicalFormulaDescriptor,
  IDescriptor
>("Inorganic");
export const isFormulationDescriptor = isOfType<
  IFormulationDescriptor,
  IDescriptor
>("Formulation");

export const isNumericDescriptor = (d?: IDescriptor): d is INumericDescriptor =>
  isIntegerDescriptor(d) || isRealDescriptor(d);

export const isValueDescriptor = (d?: IDescriptor): d is IValueDescriptor =>
  isCategoricalDescriptor(d) || isNumericDescriptor(d);

export interface IScoredDescriptors {
  descriptor: IDescriptor;
  score: number;
}
export interface IDescriptorInferenceGuesses {
  column_name: string;
  scored_descriptors: IScoredDescriptors[];
}
export interface IDescriptorInference {
  guesses: IDescriptorInferenceGuesses[];
}

export interface IGetDescriptorsFromDataSource {
  descriptors: IDescriptor[];
}

// Creates a mapping of CSV column names to Descriptor Inferences
export const sortedDescriptorInferencesByScore = (
  inferences: IDescriptorInferenceGuesses[],
) =>
  inferences.reduce(
    (acc, inference) => {
      acc[inference.column_name] = {
        ...inference,
        scored_descriptors: [...inference.scored_descriptors].sort(
          (x1, x2) => x2.score - x1.score,
        ),
      };
      return acc;
    },
    {} as Partial<Record<string, IDescriptorInferenceGuesses>>,
  );

// Creates a mapping of CSV column names to Descriptors
export const getColumnDefinitionsFromDescriptorInferences = (
  inferences: Partial<Record<string, IDescriptorInferenceGuesses>>,
) =>
  Object.values(inferences).reduce(
    (acc, guess: IDescriptorInferenceGuesses) => {
      const { column_name, scored_descriptors } = guess;
      acc[column_name] = scored_descriptors[0].descriptor;
      return acc;
    },
    {} as Partial<Record<string, IDescriptor>>,
  );

/**
 * There are some legacy persisted descriptors that were created with an outdated
 * shape of { category: string } instead of { type: string }
 *
 * We need to transform these descriptors on load to ensure they work with the app
 *
 * Purposely not typed because these incoming payloads do not adhere to the
 * IDescriptor interface
 *
 * These transform functions mutate the original object in case of Symbol or
 * hidden props we do not want to lose (there are, trust me)
 */
export function transformDescriptor(descriptor: {
  type?: string;
  category?: string;
}): void {
  if (!descriptor.type) {
    descriptor.type = descriptor.category;
  }
}

export interface FormulationRelationsResponse {
  // this is the descriptor key of the formulation
  descriptorKey: string;
  // this represents the relations for the formulation descriptor
  hierarchical: FormulationRelations;
  // this represents the relations for the simple mixture formulation descriptor
  flattened: FormulationRelations;
}
export interface FormulationRelations {
  // list of ingredients in a formulation descriptor
  ingredients: string[];
  // key is the label and the array is the ingredients the label applies to
  labels: Partial<Record<string, string[]>>;
  properties: FormulationRelationsProperties;
}

/** corresponds to the `FormulationRelations` keys in `FormulationRelationsResponse` */
export type FormulationLevel = "flattened" | "hierarchical";

export interface FormulationRelationsProperties {
  // key is the label and the array is the descriptor_key of labeled properties
  labeled: Partial<Record<string, string[]>>;
  // list of descriptor_keys for unlabeled properties
  unlabeled: string[];
}

export const getDescriptorKey = (d: { descriptor_key: string }) =>
  d.descriptor_key;

export const descriptorComparator = alphabetize<IDescriptor>(getDescriptorKey);

/**
 * Accepts an array of descriptors and returns a map with descriptors
 * as keys and units as values
 */
export function getUnitMap(
  descriptors: IDescriptor[],
  formattedUnitsLookup?: FormattedUnitsLookup,
): Partial<Record<string, string>> {
  return Object.fromEntries(
    descriptors
      .filter(isRealDescriptor)
      .map(({ descriptor_key, units }) => [
        descriptor_key,
        formattedUnitsLookup?.[units] ?? units,
      ]),
  );
}

export function getDescriptorUnits(descriptor?: IDescriptor) {
  return isRealDescriptor(descriptor) ? descriptor.units : undefined;
}
